<template>
  <div class="template-container">
    <div class="card-black">
      <div class="card-header">
        <!-- Per tornare indietro -->
        <a href="#/user" class="icon-link">
          <span class="material-icons">chevron_left</span>
        </a>
        <span class="white-text">Fusco Fit</span>
        <!-- Per andare alla home -->
        <a href="#/user" class="icon-link">
          <span class="material-icons">home</span>
        </a>
      </div>
      <div v-if="isLoading" class="loader">
        <p>Caricamento...</p>
      </div>
      <div v-else>
        <div class="titolo-card-mobile">
          <h1>ASSISTENZA TECNICA</h1>
        </div>

        <div v-if="isFormVisible" class="create-ticket">
          <p>Inizia la conversazione descrivendo la problematica, ti risponderemo prima possibile.</p>
          <form @submit.prevent="createTicket">
            <textarea v-model="description" placeholder="Descrizione"></textarea>
            <div v-if="alertMessage" :class="{'alert-success': alertSuccess, 'alert-error': !alertSuccess}">
              {{ alertMessage }}
            </div>
            <div class="button-container">
              <button type="submit" :disabled="isButtonDisabled">Invia</button>
            </div>
          </form>
        </div>
        <div class="ticket-container">
          <h2>Qui troverai la conversazione con il nostro team tecnico ed alcune notifiche</h2>
          <table>
            <tbody>
              <tr v-for="ticket in userTickets" :key="ticket.id" @click="viewConversation(ticket.id)" style="cursor: pointer;">
                <td data-label="Titolo"><strong>{{ ticket.title }}</strong><br>{{ formatDate(ticket.lastUpdate) }}</td>
                <td data-label="Stato">{{ ticket.status }}</td>
                <td class="mobile-hidden">
                  <button @click.stop="viewConversation(ticket.id)" :class="{'green': ticket.status === 'Risposto', 'yellow': ticket.status === 'Non Risposto',  'orange': ticket.status === 'Aperto', 'red': ticket.status === 'Completato'}">
                    <span class="material-icons">chevron_right</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import {
  createUserTechnicalTicket,
  resolveUserTechnicalTicket,
} from '@/lib/userTechnicalTicket';

export default {
  setup() {
    const userTickets = ref([]);
    const title = ref('');
    const description = ref('');
    const alertMessage = ref('');
    const alertSuccess = ref(false);
    const isButtonDisabled = ref(false);
    const isFormVisible = ref(true); // Variabile di stato per la visibilità del form
    const isLoading = ref(true); // Variabile di stato per il caricamento
    const authLoading = ref(true); // Variabile di stato per il caricamento dell'autenticazione
    const router = useRouter();

    const viewConversation = (ticketId) => {
      router.push({ name: 'UserTicketConversationTecnico', params: { ticketId } });
    };

    const handleLogout = async () => {
      const auth = getAuth();
      try {
        await signOut(auth);
        router.push('/');
      } catch (error) {
        console.error("Errore durante il logout:", error);
      }
    };

    const fetchTickets = async () => {
      isLoading.value = true; // Imposta il loader durante il caricamento
      const auth = getAuth();
      const currentUserId = auth.currentUser?.uid;
      if (!currentUserId) {
        console.error("Utente non autenticato");
        isLoading.value = false; // Disabilita il loader se non autenticato
        return;
      }

      userTickets.value = []; // Svuota l'array prima di ricaricarlo

      try {
        const db = getFirestore();
        const ticket = await resolveUserTechnicalTicket(db, currentUserId);

        if (ticket?.id) {
          userTickets.value = [ticket];
          isFormVisible.value = false;
          router.replace({
            name: 'UserTicketConversationTecnico',
            params: { ticketId: ticket.id },
          });
          return;
        }

        isFormVisible.value = true;
      } catch (error) {
        console.error('Errore nel recupero del ticket tecnico:', error);
        alertMessage.value = 'IMPOSSIBILE RECUPERARE LA CONVERSAZIONE TECNICA';
        alertSuccess.value = false;
        isFormVisible.value = false;
      } finally {
        isLoading.value = false; // Disabilita il loader dopo il caricamento
      }
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      return date.toLocaleDateString("it-IT") + ' ' + date.toLocaleTimeString("it-IT");
    };

    const createTicket = async () => {
      isButtonDisabled.value = true; // Disabilita il bottone

      const db = getFirestore();
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!description.value.trim()) {
        alertMessage.value = 'INSERISCI UNA DESCRIZIONE';
        alertSuccess.value = false;
        isButtonDisabled.value = false;
        return;
      }

      try {
        const { ticketId } = await createUserTechnicalTicket(db, {
          userId,
          title: title.value,
          description: description.value,
        });
        console.log('Ticket tecnico pronto. ID:', ticketId);
        alertMessage.value = 'TICKET CREATO CON SUCCESSO';
        alertSuccess.value = true;
        isFormVisible.value = false;
        router.replace({
          name: 'UserTicketConversationTecnico',
          params: { ticketId },
        });
      } catch (error) {
        alertMessage.value = 'IMPOSSIBILE CREARE UN TICKET TECNICO';
        alertSuccess.value = false;
        console.error('Errore nella creazione del ticket:', error);
      } finally {
        isButtonDisabled.value = false;
      }
    };

    const redirectToHome = () => {
      router.push('/');
    };

    onMounted(() => {
      // Verifica lo stato di autenticazione prima di recuperare i ticket
      const auth = getAuth();
      onAuthStateChanged(auth, (user) => {
        if (user) {
          fetchTickets();
        } else {
          isLoading.value = false;
          console.error("Utente non autenticato");
        }
        authLoading.value = false; // Disabilita il loader di autenticazione
      });
    });

    return {
      userTickets,
      title,
      description,
      createTicket,
      alertMessage,
      alertSuccess,
      isButtonDisabled,
      isFormVisible, // Aggiungi la variabile di stato al return
      isLoading, // Aggiungi la variabile di stato al return
      authLoading,
      viewConversation,
      handleLogout,
      formatDate,
      redirectToHome,
    };
  },
};
</script>

<style scoped>


tbody tr td,
tbody tr th {
  color: #ffffff;

}

table{
  background-color: #2a2a2a;
}

.loader {
  text-align: center;
  font-size: 20px;
  padding: 20px;
}
.refresh-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
}
.auth-failed-message {
  text-align: center;
  padding: 20px;
}

.titolo-card-mobile {
  margin-top: 20px;
}

.material-icons{
  color:#dedede
}

.white-text {
  color: white;
}
</style>
